// Purpose: DataTable primitive for list views (Shares, Dropboxes,
// admin Users, admin Audit, admin Routing). Renders a token-styled
// table with sticky header, row hover, optional per-row navigation
// (rows-as-buttons via `onRowClick`), and a responsive stack-below-md
// mode where each row becomes a card-like block on narrow viewports.
//
// Each column is a <Column<T>> descriptor: header label + a cell
// renderer. Generics so TypeScript can infer the row shape at the
// callsite.

import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Return the cell content for this row. */
  cell: (row: T) => ReactNode;
  /** Optional CSS width (any valid CSS width value). */
  width?: string;
  /** Optional CSS alignment. Defaults to left. */
  align?: 'left' | 'right' | 'center';
  /** Hide on narrow viewports (the stack layout drops the column). */
  hideOnMobile?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Rendered above the table when rows.length is 0. */
  empty?: ReactNode;
  className?: string;
  /** Optional caption for screen readers. */
  caption?: ReactNode;
}

const ALIGN: Record<'left' | 'right' | 'center', string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  empty,
  className,
  caption,
}: DataTableProps<T>) {
  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }
  return (
    <div
      className={[
        'w-full overflow-x-auto rounded-lg border border-border-default bg-bg-secondary',
        className ?? '',
      ].join(' ')}
    >
      <table className="w-full border-collapse text-body-m">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="border-b border-border-default bg-bg-tertiary/40">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={col.width ? { width: col.width } : undefined}
                className={[
                  'text-label uppercase text-fg-muted',
                  'h-10 px-4 font-semibold',
                  ALIGN[col.align ?? 'left'],
                  col.hideOnMobile ? 'hidden md:table-cell' : '',
                ].join(' ')}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const rowClickable = Boolean(onRowClick);
            return (
              <tr
                key={getRowKey(row)}
                onClick={rowClickable ? () => onRowClick?.(row) : undefined}
                className={[
                  idx !== rows.length - 1 ? 'border-b border-border-default' : '',
                  'text-fg-primary',
                  rowClickable
                    ? 'cursor-pointer transition-colors duration-fast ease-out hover:bg-bg-tertiary/40'
                    : '',
                ].join(' ')}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={[
                      'px-4 h-12 align-middle',
                      ALIGN[col.align ?? 'left'],
                      col.hideOnMobile ? 'hidden md:table-cell' : '',
                    ].join(' ')}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
